<template>
  <div class="app-order-summary">
    <slot name="txtBefore"></slot>
    <div class="summary-content personal-data-content">
      <ShowPersonalData :personalData="personalData">
        <template v-for="slot in possibleChildSlotsFor('PersonalData')" v-slot:[`${slot.child}`]>
          <slot :name="`${slot.parent}`"></slot>
        </template>
      </ShowPersonalData>
    </div>
    <div class="summary-content ident-data-content">
      <ShowIdentData :identData="identData">
        <template v-for="slot in possibleChildSlotsFor('IdentData')" v-slot:[`${slot.child}`]>
          <slot :name="`${slot.parent}`"></slot>
        </template>
      </ShowIdentData>
    </div>
    <div class="summary-content mnp-data-content" v-if="mnpData.active">
      <ShowMnpData
        :mnpData ="mnpData"
      >
        <template v-for="slot in possibleChildSlotsFor('MnpData')" v-slot:[`${slot.child}`]>
          <slot :name="`${slot.parent}`"></slot>
        </template>
      </ShowMnpData>
    </div>
    <div class="summary-content payment-data-content">
      <ShowPaymentData
        :paymentData="paymentData"
        :currentStep="getStep"
      >
        <template v-for="slot in possibleChildSlotsFor('PaymentData')" v-slot:[`${slot.child}`]>
          <slot :name="`${slot.parent}`"></slot>
        </template>
      </ShowPaymentData>
    </div>
    <div class="summary-content top-up-data-content" v-if="hasTopUp">
      <ShowTopUpData>
        <template v-for="slot in possibleChildSlotsFor('TopUpData')" v-slot:[`${slot.child}`]>
          <slot :name="`${slot.parent}`"></slot>
        </template>
      </ShowTopUpData>
    </div>
    <div class="summary-content consent-data">
      <ShowConsentData
        v-if="!isSuccessStep"
        :consentAGB.sync="consent.agb"
        :consentInformationArticle.sync="consent.informationArticle"
        :selectedInfoExchangeConsent.sync="consent.exchange"
        :selectedContactConsent.sync="consent.contact"
        :contractTermsVersion.sync="consent.contractTermsVersion"
      >
        <template v-for="slot in possibleChildSlotsFor('ConsentData')" v-slot:[`${slot.child}`]>
          <slot :name="`${slot.parent}`"></slot>
        </template>
      </ShowConsentData>
    </div>
    <div class="summary-content shopping-card">
      <slot name="txtBeforeShoppingCard"><h2>Deine Bestellung</h2></slot>
      <ShoppingCard
        :presalesTariff="presalesTariff"
        :presalesTariffOption="presalesTariffOption"
        :tariffsExtra="tariffsExtra"
        :tariffOptionsExtra="tariffOptionsExtra"
        :mnpActive="mnpData.active"
        :referFriend="referFriend"
      >
        <template v-for="slot in possibleChildSlotsFor('ShoppingCard')" v-slot:[`${slot.child}`]>
          <slot :name="`${slot.parent}`"></slot>
        </template>
      </ShoppingCard>
    </div>
    <oppwa-c-c v-if="requiresPaypalFlow" available-credit-cards="PAYPAL" :token="paypalPayOnToken" :redirect-url="currentUrl" :key="paypalPayOnToken">
      <template v-for="slot in possibleChildSlotsFor('Oppwa')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </oppwa-c-c>
    <div class="order-step" v-if="!isSuccessStep">
      <b-overlay class="btn-next-overlay" :show="loading" spinner-small>
        <b-button @click="next" :disabled="loading" class="btn-next" type="button" variant="primary">
          <slot name="btnNext">Jetzt kostenpflichtig bestellen</slot>
        </b-button>
      </b-overlay>
    </div>
    <slot name="txtMandatory" v-if="!isSuccessStep"></slot>
    <slot name="txtAfter"></slot>
  </div>
</template>
<script>

import { BButton } from 'bootstrap-vue'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import slotProxy from '@/mixins/slotProxy'
import ShowPersonalData from '@/components/process/FYVE/Order/PersonalData/ShowPersonalData.vue'
import ShowIdentData from '@/components/process/FYVE/Order/PersonalData/ShowIdentData.vue'
import ShowPaymentData from '@/components/process/FYVE/Order/Payment/ShowPaymentData.vue'
import ShowTopUpData from '@/components/process/FYVE/Order/TopUp/ShowTopUpData.vue'
import ShowConsentData from '@/components/process/FYVE/Order/Consent/ConsentData.vue'
import ShowMnpData from '@/components/process/FYVE/Order/Options/ShowMnpData.vue'
import ShoppingCard from '@/components/process/FYVE/Order/ShoppingCard.vue'
import OppwaCC from '@/components/generic/OppwaCC.vue'
import paypalReturn from '@/mixins/paypalReturn'
import gtmEvent from '@/mixins/gtmEvent'

export const stepName = 'summary'
export default {
  name: 'OrderSummary',
  components: {
    OppwaCC, BButton, ShowPersonalData, ShowIdentData, ShowPaymentData, ShowTopUpData, ShowConsentData, ShowMnpData, ShoppingCard
  },
  data () {
    return {
      loading: false,
      consent: {
        agb: false,
        informationArticle: false,
        exchange: false,
        contact: false,
        contractTermsVersion: null
      }
    }
  },
  computed: {
    ...mapGetters('orderProduct', [
      'getStep',
      'requiresPaypalFlow',
      'paypalPayOnToken',
      'paypalFinished',
      'getContractId'
    ]),
    ...mapGetters('orderProduct/personalData', [
      'customerCore', 'identificationFull'
    ]),
    ...mapGetters('orderProduct/payment', [
      'payment'
    ]),
    ...mapGetters('orderProduct/topUp', [
      'hasTopUp'
    ]),
    ...mapGetters('orderProduct/gtmEventData', [
      'getTariffName',
      'getTariffProductNumber',
      'getTariffPrice',
      'getTariffShippingCosts'
    ]),
    personalData: function () {
      return {
        person: this.customerCore
      }
    },
    identData: function () {
      return this.identificationFull
    },
    paymentData: function () {
      return {
        ...this.payment,
        type: this.payment.paymentType
      }
    },
    isSuccessStep: function () {
      return this.getStep === 'success'
    },
    currentUrl: function () {
      if (this.paypalRedirectUrl) {
        return this.paypalRedirectUrl
      }
      return window.location.origin + window.location.pathname
    }
  },
  methods: {
    ...mapActions('orderProduct', [
      'confirmOrder'
    ]),
    ...mapMutations('orderProduct', [
      'storeConsents'
    ]),
    async next () {
      if (this.requiresPaypalFlow && this.paypalFinished === false) {
        this.preparePaypalFlow()
        return
      }
      this.loading = true
      await this.confirmOrder({ step: this.nextStep, consents: this.consent })
      this.pushEventPurchase('eec.transaction', this.getContractId, this.getTariffName, this.getTariffShippingCosts, this.getTariffProductNumber, this.getTariffPrice, 'fyve')
      this.loading = false
    },
    preparePaypalFlow () {
      // a form is embedded by oppwa script. submitting this form will trigger paypal flow
      const paypalForm = document.querySelector('form[data-action="submit-payment-PAYPAL"]')
      if (!paypalForm) {
        this.$store.commit('addError', 'Es ist ein technischer Fehler aufgetreten')
        return
      }
      this.loading = true
      // since this is the last user interaction in our summary, we must save the consents first so the selection will
      // not be lost during paypal processing
      this.storeConsents(this.consent)
      paypalForm.submit()
    },
    async confirmAfterPaypal (token, stripUrl) {
      this.loading = true
      // we do not provide consents here, because user selection was stored before paypal flow
      await this.confirmOrder({ step: this.nextStep })
      if (stripUrl) {
        this.stripUrl()
      }
      this.loading = false
    }
  },
  props: {
    nextStep: {
      type: String,
      default: 'success'
    },
    presalesTariff: {
      type: Object,
      default: () => {}
    },
    presalesTariffOption: {
      type: Object,
      default: () => {}
    },
    tariffsExtra: {
      type: Object,
      default: () => {}
    },
    tariffOptionsExtra: {
      type: Object,
      default: () => {}
    },
    mnpData: {
      type: Object
    },
    paypalRedirectUrl: {
      type: String
    },
    referFriend: {
      type: Boolean
    }
  },
  mixins: [
    slotProxy(),
    paypalReturn('confirmAfterPaypal'),
    gtmEvent
  ]
}
</script>
